import React, { useState } from "react"
import { HandThumbUpIcon, HandThumbDownIcon, ChatBubbleLeftIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Value from "aldoo-ra/Value"
import useT from "../../hooks/useT"
import { SubmitReaction, SubmitComment, LoadComments } from "../../storeApi"
import Chip from "../Chip"
import Spinner from "../Spinner"
import UserAvatar from "../UserAvatar"

const COMMENTS_PER_LOAD = 10

// a reply from the seller or the store goes under their name, not the person's who typed it
const replyAuthor = (comment) => (comment.replyAs === "aldoo" ? "Aldoo" : comment.org?.name || "")

// helpful / unhelpful / comments chips + inline comment list and composer
export function Reactions({ target, targetModel }) {
  const t = useT()
  const [auth] = PlatformAuth.useAuth()
  const [profile] = Value("user-profile")
  const canReact = !!auth && !!target.user && auth._id !== target.user._id

  const [myReaction, setMyReaction] = useState(target.myReaction)
  const [reactions, setReactions] = useState(target.reactions || {})
  const [comments, setComments] = useState(null) // null = not loaded
  const [total, setTotal] = useState(target.comments || 0)
  const [page, setPage] = useState(0)
  const [pending, setPending] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [composerOpen, setComposerOpen] = useState(false)
  const [text, setText] = useState("")
  const [menuOpen, setMenuOpen] = useState(false)

  const react = (reaction) => {
    if (!canReact) return
    const next = { ...reactions }
    if (myReaction) next[myReaction] = Math.max(0, (next[myReaction] || 0) - 1)
    const toggledOff = reaction === myReaction
    if (!toggledOff) next[reaction] = (next[reaction] || 0) + 1
    setReactions(next)
    setMyReaction(toggledOff ? undefined : reaction)
    SubmitReaction(target._id, targetModel, toggledOff ? false : reaction).catch(() => {})
  }

  const loadMore = async () => {
    setPending(true)
    try {
      const result = await LoadComments(target._id, page, COMMENTS_PER_LOAD)
      setComments([...(comments || []), ...(result?.comments || [])])
      setTotal(result?.total ?? total)
      setPage(page + 1)
    } finally {
      setPending(false)
    }
  }

  const toggleComments = () => {
    if (hidden) return setHidden(false)
    if (comments) return setHidden(true)
    if (total > 0) loadMore()
  }

  // the comment chip: shows the thread, and for someone who may comment it
  // also opens the composer — the way in without the ⋮ menu
  const onCommentChip = () => {
    if (canReact) setComposerOpen(true)
    if (total > 0) toggleComments()
  }

  const submitComment = async () => {
    if (!text.trim()) return
    setPending(true)
    try {
      const result = await SubmitComment(target._id, targetModel, text.trim())
      const comment = { _id: result?._id || `local-${Date.now()}`, user: { _id: auth?._id, ...(profile || {}) }, created: Date.now(), text: text.trim(), comments: 0 }
      setComments([...(comments || []), comment])
      setTotal(total + 1)
      setHidden(false)
      setText("")
      setComposerOpen(false)
    } finally {
      setPending(false)
    }
  }

  const chip = (active) => (active ? "primary" : "ghost")
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center gap-1.5">
        <Chip tone={chip(myReaction === "helpful")} size="sm" onClick={canReact ? () => react("helpful") : undefined} icon={<HandThumbUpIcon className="w-4 h-4" />} label={reactions.helpful || ""} />
        <Chip tone={chip(myReaction === "unhelpful")} size="sm" onClick={canReact ? () => react("unhelpful") : undefined} icon={<HandThumbDownIcon className="w-4 h-4" />} label={reactions.unhelpful || ""} />
        <Chip tone={chip(!hidden && comments?.length > 0)} size="sm" onClick={total > 0 || canReact ? onCommentChip : undefined} icon={<ChatBubbleLeftIcon className="w-4 h-4" />} label={String(total)} />
        {canReact && (
          <div className="relative ml-auto">
            <button type="button" className="p-1 rounded-full hover:bg-neutral" onClick={() => setMenuOpen((o) => !o)} aria-label="more">
              <EllipsisVerticalIcon className="w-5 h-5" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full z-20 min-w-[200px] rounded-md border border-bordered bg-paper shadow-lg py-1 text-[14px]">
                <button type="button" className="block w-full text-left px-4 py-2 hover:bg-neutral" onClick={() => { react("inappropriate"); setMenuOpen(false) }}>
                  {myReaction === "inappropriate" ? t`Unflag Inappropriate` : t`Flag Inappropriate`}
                </button>
                <button type="button" className="block w-full text-left px-4 py-2 hover:bg-neutral" onClick={() => { setComposerOpen(true); setMenuOpen(false) }}>
                  {t`Leave a Comment`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {!hidden && comments && (
        <div className="flex flex-col gap-2 ml-8">
          {comments.map((comment) => (
            <div key={comment._id} className="flex flex-col gap-2 rounded-lg bg-neutral/60 px-4 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <UserAvatar user={comment.replyAs ? { name: replyAuthor(comment) } : comment.user} size={32} />
                <span className="text-[14px] font-semibold text-primary">{comment.replyAs ? replyAuthor(comment) : comment.user?.name}</span>
                {comment.replyAs === "vendor" && <span className="text-[12px] text-text_secondary">{t`Seller`}</span>}
              </div>
              <p className="m-0 text-[15px]">{comment.text}</p>
              <Reactions target={comment} targetModel="UserComment" />
            </div>
          ))}
          {pending && <Spinner className="py-2" size={20} />}
          {!pending && total > comments.length && comments.length > 0 && (
            <button type="button" className="text-[14px] text-primary hover:underline self-start" onClick={loadMore}>
              {t("Load %d more comments", total - comments.length)}
            </button>
          )}
        </div>
      )}

      {composerOpen && (
        <div className="flex flex-col gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} disabled={pending} autoFocus className="w-full rounded-md border border-bordered p-3 text-[15px] focus:outline-none focus:border-primary" />
          <div className="flex gap-2">
            <button type="button" disabled={pending} onClick={submitComment} className="h-9 px-4 rounded-full bg-primary text-white text-[14px] font-bold disabled:opacity-50">{t`Submit`}</button>
            <button type="button" disabled={pending} onClick={() => setComposerOpen(false)} className="h-9 px-4 rounded-full border border-bordered text-[14px]">{t`Cancel`}</button>
          </div>
        </div>
      )}
    </div>
  )
}
